import { Card, CardBody, Tab, Tabs } from '@heroui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { FormFooter } from '../../../../components/FormFooter';

import { useActiveTab } from './hooks';
import { useSupplierForm, SupplierFormProvider } from './SupplierFormProvider';
import {
  OrderPreferencesTab,
  ProfileTab,
  SettingsTab,
  NotificationsTab,
  CatalogTab,
} from './tabs';
import { TabType, tabLabels, type TabProps } from './types';

// Internal component to render content that needs form context
const SupplierFormContent: React.FC<TabProps> = ({ mode, supplier }) => {
  const { activeTab, setActiveTab, availableTabs } = useActiveTab(mode);
  const {
    formMethods: {
      handleSubmit,
      formState: { isSubmitting, isDirty },
    },
  } = useSupplierForm();
  const navigate = useNavigate();

  const renderTabContent = (tab: TabType) => {
    switch (tab) {
      case TabType.PROFILE:
        return <ProfileTab mode={mode} supplier={supplier} />;
      case TabType.ORDER_PREFERENCES:
        return <OrderPreferencesTab mode={mode} supplier={supplier} />;
      case TabType.CATALOG:
        return <CatalogTab supplier={supplier} />;
      case TabType.SETTINGS:
        return <SettingsTab mode={mode} supplier={supplier} />;
      case TabType.NOTIFICATIONS:
        return <NotificationsTab mode={mode} supplier={supplier} />;
      default:
        return (
          <div className="p-6">
            <p>Content for {tabLabels[tab]}</p>
          </div>
        );
    }
  };

  const handleSave = handleSubmit((data) => {
    // TODO: Implement actual save logic here
  });

  const handleCancel = () => {
    navigate('/suppliers');
  };

  return (
    <>
      <Card id="supplier-form">
        <CardBody className="p-4">
          <Tabs
            aria-label="Distributor sections"
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as TabType)}
            variant="underlined"
            classNames={{
              tabList: 'gap-6 w-full relative px-6 border-b border-divider',
              cursor: 'w-full',
              tab: 'max-w-fit px-2 h-12',
              tabContent: 'group-data-[selected=true]:text-primary',
            }}
          >
            {availableTabs.map((tab) => (
              <Tab key={tab} title={tabLabels[tab]}>
                {renderTabContent(tab)}
              </Tab>
            ))}
          </Tabs>
        </CardBody>
      </Card>
      <FormFooter
        saveText={mode === 'create' ? 'Create Distributor' : 'Save Changes'}
        onSave={handleSave}
        onCancel={handleCancel}
        isSaving={isSubmitting}
        isSaveDisabled={!isDirty || isSubmitting}
        isCancelDisabled={isSubmitting}
      />
    </>
  );
};

export const SupplierForm: React.FC<TabProps> = ({ mode, supplier }) => {
  return (
    <SupplierFormProvider mode={mode} supplier={supplier}>
      <SupplierFormContent mode={mode} supplier={supplier} />
    </SupplierFormProvider>
  );
};
